import type { ColorScheme } from "@/hooks/useColorScheme";
import {
  HighchartsXAxisBottomDefault,
  HighchartsXAxisTop,
  HighchartsYAxis,
} from "@/overlay/components/CommonParts/PlotHighchart/utils/chart-options/axis-default";
import { handleLegendItemClick } from "@/overlay/components/CommonParts/PlotHighchart/utils/chart-options/legends-helper";
import type { CustomOptions } from "@/overlay/components/CommonParts/PlotHighchart/utils/custom-visualization";
import type { PlotTimeRange } from "@/overlay/components/CommonParts/PlotHighchart/utils/time-range";
import { getTooltipDateTimeLabelFormats } from "@/utility/highchartsTooltipTime";
import type { I18n } from "@lingui/core";
import { type ElementStyle, GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { Options, XAxisOptions, YAxisOptions } from "highcharts";
import type { PlotlineProps } from "./plotline";

type ChartOptionsProps = {
  yAxisOptions: YAxisOptions[];
  legendStyle?: ElementStyle | null;
  userTimeRange: Partial<PlotTimeRange>;
  hideLegend: boolean | undefined;
  plotlineProps: PlotlineProps[];
  i18n: I18n;
  colorScheme?: ColorScheme;
  timezone?: GuiTimeZone;
};
export const DEFAULT_FONT_SIZE = 12;

// Keeps the chart background in sync with the app theme instead of relying on Highcharts' own palette background.
export const CHART_BACKGROUND_COLOR = "var(--color-bg-primary)";

// TODO: better weekend view - plotOptions:
export const HighchartsChartOptionsDefault = (
  plotlineProps: PlotlineProps[],
  i18n: I18n,
  colorScheme: ColorScheme = "light",
): Options => ({
  // https://api.highcharts.com/highcharts/boost default is 50
  boost: { seriesThreshold: 18 },
  chart: {
    zooming: { type: "xy" },
    animation: false,
    allowMutatingData: false,
    backgroundColor: CHART_BACKGROUND_COLOR,
  },
  title: {
    text: undefined,
  },
  credits: {
    enabled: false,
  },
  palette: { colorScheme },
  yAxis: HighchartsYAxis,
  xAxis: [{ ...HighchartsXAxisBottomDefault(plotlineProps, i18n) }, { ...HighchartsXAxisTop }],
  legend: {
    events: {
      itemClick: handleLegendItemClick,
    },
  },
  series: [],
  plotOptions: {
    // @ts-ignore custom series registration
    plotwindbarb: {
      tooltip: {
        valueDecimals: 2,
      },
      // fix for limit over 1000 https://www.highcharts.com/forum/viewtopic.php?t=52638
      turboThreshold: 0,
      grouping: true,
    },
    // @ts-ignore
    weathersymbol: {
      tooltip: {
        valueDecimals: 2,
      },
      turboThreshold: 0,
      grouping: true,
    },
  },
});

/**
 * Create a base chart options WITHOUT data
 * @param props
 * @returns
 */
export function getChartOptions({
  legendStyle,
  hideLegend,
  yAxisOptions,
  userTimeRange,
  plotlineProps,
  i18n,
  colorScheme,
  timezone = GuiTimeZone.utc,
}: ChartOptionsProps): CustomOptions {
  const baseChartOptions: CustomOptions = {
    ...HighchartsChartOptionsDefault(plotlineProps, i18n, colorScheme),
    yAxis: yAxisOptions,
  };

  const legend = {
    ...baseChartOptions.legend,
    enabled: !hideLegend,
    ...(legendStyle && {
      itemStyle: {
        ...legendStyle,
        fontSize: `${legendStyle.fontSize || DEFAULT_FONT_SIZE}px`,
        fontWeight: `${legendStyle.fontWeight}`,
        color: legendStyle.color ?? undefined,
        textDecoration: legendStyle.textDecoration ?? undefined,
        fontStyle: legendStyle.fontStyle ?? undefined,
      },
    }),
  };

  // we set xAxis.min if a user time range is set
  const updatedXAxis = (baseChartOptions.xAxis as XAxisOptions[]).map((xAxisOption) => ({
    ...xAxisOption,
    min: userTimeRange.timeFrom?.toMillis(), // Only set `min` if timeFrom is provided
  }));

  return {
    ...baseChartOptions,
    legend: legend,
    accessibility: { enabled: false },
    boost: { seriesThreshold: 18 },
    xAxis: updatedXAxis,
    tooltip: {
      shared: true,
      useHTML: true,
      dateTimeLabelFormats: getTooltipDateTimeLabelFormats(timezone),
    },
  };
}
